import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';

import { SubmitHandler, useForm } from 'react-hook-form';
import { Grid, TextField } from '@mui/material';
import ContainerBox from 'components/ContainerBox';

import { createCarrier, updateCarrier } from 'common/api/frontend';
import { Carrier } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { push, Routes } from 'routing';
import errorHandler from 'common/services/errorHandler';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import Banner from 'components/Banner';

interface CarrierFormData {
	name: string;
	locationIds: string[];
}

export interface FormHandler {
	errors: boolean;
	handleSubmit: () => void;
}

interface Props {
	value?: Carrier;
	setLoading?: (loading: boolean) => void;
	setHasChanges?: (hasChanges: boolean) => void;
}

const CarrierForm = forwardRef((props: Props, ref) => {
	const { value, setLoading, setHasChanges } = props;
	const { t } = useTranslation();

	const [formError, setFormError] = useState<string>('');
	const shopId = useSelector(ShopSelectors.activeShopId);
	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const activeShopId = useSelector(UserSelectors.userActiveLocationId)!;

	const { register, handleSubmit, reset, formState } = useForm<CarrierFormData>({
		defaultValues: {
			name: value?.name?.def ?? '',
			locationIds: [activeShopId],
		},
		mode: 'onChange',
	});

	useEffect(() => {
		if (setHasChanges) {
			setHasChanges(formState.isDirty);
		}
	}, [formState.isDirty, setHasChanges]);

	useImperativeHandle(ref, () => ({
		errors: Object.keys(formState.errors).length > 0,
		handleSubmit: handleSubmit(handleSubmitForm),
	}));

	if (!shopId) return null;

	const nameRules = {
		required: t('common:validation.cannotBeEmpty', {
			defaultValue: '{{field}} cannot be empty',
			field: t('delivery.name', 'Name'),
		}),
		validate: {
			maxLength: (value: string) =>
				value.length <= 100 ||
				t('common:validation.lengthAtMost', {
					defaultValue: '{{field}} can be at most {{int}} characters',
					field: t('delivery.name', 'Name'),
					int: 100,
				}),
		},
	};

	const getDataToSubmit = (formData: CarrierFormData): Omit<Carrier, 'id'> => {
		const { name } = formData;
		return {
			shopId,
			createdAt: value?.createdAt ?? new Date().toISOString(),
			name: {
				...value?.name,
				def: name,
				...(defaultLanguage && { [defaultLanguage]: name }),
			},
			locationIds: [activeShopId],
		};
	};

	const handleSubmitForm: SubmitHandler<CarrierFormData> = async (data) => {
		setLoading?.(true);
		try {
			const carrier = getDataToSubmit(data);
			if (value) {
				await updateCarrier(carrier, value.id);
				reset(data);
			} else {
				await createCarrier(carrier);
				push(Routes.DELIVERY);
			}
		} catch (e) {
			errorHandler.report(e);
			setFormError(e.message);
		} finally {
			setLoading?.(false);
		}
	};

	return (
		<>
			{!!formError && <Banner variant="danger" title={formError} />}
			<ContainerBox padding="normal">
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<TextField
							label={t('delivery.name', 'Name')}
							variant="standard"
							type="text"
							fullWidth
							autoFocus={!value}
							placeholder={t('delivery.carrierNamePlaceholder', 'E.g. Our precious van')}
							error={!!formState.errors.name}
							helperText={formState.errors.name?.message}
							{...register('name', nameRules)}
						/>
					</Grid>
				</Grid>
			</ContainerBox>
		</>
	);
});

export default CarrierForm;
